import List from "sap/m/List";

export default class DragAutoScroll {

  private static readonly TRIGGER_BUFFER = 60; // Puffer an der inneren Kante, damit der Trigger beim Scrollen nicht sofort wieder abbricht
  private static readonly MIN_INTERVAL = 150; // ms zwischen zwei Sprüngen zum nächsten Item, steuert allein die Geschwindigkeit
  private static readonly VISIBILITY_TOLERANCE = 1; // Toleranz in px gegen Subpixel-Rundung an der Kante

  private lastScrollTimestamp = 0;

  // headerElement: optionales Element (z.B. der sticky ObjectPageHeader), das im selben Scroll-Container
  // über der Liste liegt - die sichtbare obere Kante ist dann dessen Unterkante, nicht die des Containers
  public handleDragOver(browserEvent: DragEvent, list: List, headerElement?: Element | null): void {
    const scrollParent = this.getScrollParent(list.getDomRef());
    if (!scrollParent) {
      return;
    }

    const containerRect = scrollParent.getBoundingClientRect();
    const headerRect = headerElement?.getBoundingClientRect();
    const topBoundary = headerRect ? Math.max(containerRect.top, headerRect.bottom) : containerRect.top;
    const bottomBoundary = containerRect.bottom;

    const itemDomRefs = list.getItems()
      .map(item => item.getDomRef())
      .filter((domRef): domRef is Element => !!domRef);

    const visibleRange = this.getVisibleItemRange(topBoundary, bottomBoundary, itemDomRefs);
    if (!visibleRange) {
      return;
    }
    const [firstVisible, lastVisible] = visibleRange;

    const now = Date.now();
    if (this.lastScrollTimestamp && now - this.lastScrollTimestamp < DragAutoScroll.MIN_INTERVAL) {
      return;
    }

    const firstVisibleRect = itemDomRefs[firstVisible].getBoundingClientRect();
    const lastVisibleRect = itemDomRefs[lastVisible].getBoundingClientRect();

    // springt jeweils genau so weit, dass das nächste Item vollständig sichtbar wird - kein fester
    // Schritt/Restkorrektur mehr nötig, die Geschwindigkeit ergibt sich allein aus MIN_INTERVAL. Nach
    // oben ist "sichtbar" die Unterkante des Headers (falls vorhanden), nicht die des Containers.
    let newScrollTop: number;
    if (firstVisible > 0 && browserEvent.clientY <= firstVisibleRect.bottom + DragAutoScroll.TRIGGER_BUFFER) {
      const nextItemRect = itemDomRefs[firstVisible - 1].getBoundingClientRect();
      newScrollTop = scrollParent.scrollTop + (nextItemRect.top - topBoundary);
    } else if (lastVisible < itemDomRefs.length - 1 && browserEvent.clientY >= lastVisibleRect.top - DragAutoScroll.TRIGGER_BUFFER) {
      const nextItemRect = itemDomRefs[lastVisible + 1].getBoundingClientRect();
      newScrollTop = scrollParent.scrollTop + (nextItemRect.bottom - bottomBoundary);
    } else {
      return;
    }

    this.lastScrollTimestamp = now;
    scrollParent.scrollTop = newScrollTop;
  }

  // liefert [ersterIndex, letzterIndex] des zusammenhängenden Blocks vollständig
  // sichtbarer Items zwischen topBoundary und bottomBoundary
  private getVisibleItemRange(topBoundary: number, bottomBoundary: number, itemDomRefs: Element[]): [number, number] | null {
    const isFullyVisible = (domRef: Element) => {
      const rect = domRef.getBoundingClientRect();
      return rect.top >= topBoundary - DragAutoScroll.VISIBILITY_TOLERANCE
        && rect.bottom <= bottomBoundary + DragAutoScroll.VISIBILITY_TOLERANCE;
    };

    const first = itemDomRefs.findIndex(isFullyVisible);
    if (first === -1) {
      return null;
    }

    let last = first;
    for (let i = first + 1; i < itemDomRefs.length; i++) {
      if (!isFullyVisible(itemDomRefs[i])) {
        break;
      }
      last = i;
    }

    return [first, last];
  }

  private getScrollParent(node: Element | null): Element | null {
    let parent = node?.parentElement ?? null;
    while (parent) {
      const style = window.getComputedStyle(parent);
      if (/(auto|scroll)/.test(style.overflowY) && parent.scrollHeight > parent.clientHeight) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return document.scrollingElement;
  }
}
